<div *ngIf="organization">
  <div class="row">
    <!-- Organization Information Card -->
    <div class="col-lg-6 mb-4">
      <div class="card border-0 bg-transparent shadow-none">
        <div class="card-header bg-transparent px-0">
          <h6 class="mb-0">
            <i class="bi bi-building me-2"></i>
            Organization Information
          </h6>
        </div>
        <div class="card-body px-0 list-group list-group-flush">
          <div class="list-group-item bg-transparent px-0 py-3">
            <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-2">Name</label>
            <div>{{ organization.name }}</div>
          </div>
          <div class="list-group-item bg-transparent px-0 py-3">
            <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-2">Type</label>
            <div>
              <span class="badge bg-primary">
                {{ getOrganizationTypeLabel(organization.organizationType) }}
              </span>
            </div>
          </div>
          <div class="list-group-item bg-transparent px-0 py-3" *ngIf="organization.einLast4 || organization.ein">
            <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-2">EIN</label>
            <div>{{ maskEIN(organization) }}</div>
          </div>
         <div class="list-group-item bg-transparent px-0 py-3">
          <div class="row g-3">
            <!-- Address -->
            <div class="col-sm-6">
              <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-2">Address</label>
              <div>{{ organization.streetAddressLine1 }}</div>
              <div *ngIf="organization.streetAddressLine2">{{ organization.streetAddressLine2 }}</div>
              <div>{{ organization.city }}, {{ organization.state }} {{ organization.zipCode }}</div>
              <div>{{ organization.country }}</div>
            </div>

            <!-- Coordinates -->
            <div class="col-sm-6">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <label class="small fw-semibold text-uppercase text-body-secondary mb-0">Coordinates</label>
                <button 
                  type="button" 
                  class="btn btn-sm btn-outline-secondary"
                  (click)="lookupCoordinates()"
                  [disabled]="lookingUpCoordinates || !organization.streetAddressLine1 || !organization.city || !organization.state">
                  <i class="bi bi-crosshair me-1"></i>
                  {{ organization.latitude != null ? 'Refresh' : 'Look up' }}
                </button>
              </div>
              <div><span class="text-body-secondary small me-2">Lat</span>{{ organization.latitude != null ? (organization.latitude | number:'1.5-5') : 'Not set' }}</div>
              <div><span class="text-body-secondary small me-2">Long</span>{{ organization.longitude != null ? (organization.longitude | number:'1.5-5') : 'Not set' }}</div>
              <ng-container *ngIf="organization.latitude != null && organization.longitude != null">
                <a class="small text-decoration-none d-inline-block mt-1"
                  [href]="'https://www.google.com/maps?q=' + organization.latitude + ',' + organization.longitude"
                  target="_blank" rel="noopener">
                  <i class="bi bi-geo-alt me-1"></i>View on map
                </a>
              </ng-container>
            </div>
          </div>
        </div>

          <!-- Reads from the new adn_organization_payment_accounts via the
               enriched admin endpoint (billing-payments § 2.1). Replaces
               the deprecated organization.stripeCustomerId column read.
               `as customer` / `as connect` aliasing narrows the type so
               the inner template can drop the !-chains. -->
          <ng-container *ngIf="organization.paymentAccounts?.customer as customer">
            <div class="list-group-item bg-transparent px-0 py-3" *ngIf="customer.stripeCustomerId">
              <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-2">Stripe Customer ID</label>
              <div>
                <code class="small">{{ customer.stripeCustomerId }}</code>
                <div class="text-muted small mt-1">
                  Status: {{ customer.status }}
                  <span *ngIf="customer.defaultPaymentMethodId">
                    &nbsp;·&nbsp; Default PM on file
                  </span>
                  <span *ngIf="customer.subscriptionStatus">
                    &nbsp;·&nbsp; Subscription: {{ customer.subscriptionStatus }}
                  </span>
                </div>
              </div>
            </div>
          </ng-container>
          <!-- Deprecation-window fallback: if the response predates the
               adn_organization_payment_accounts enrichment, fall back to
               the (deprecated) organization.stripeCustomerId column so
               admins can still see the customer id. Delete this block
               when the deprecated column is dropped. -->
          <div
            class="list-group-item bg-transparent px-0 py-3"
            *ngIf="!organization.paymentAccounts?.customer && organization.stripeCustomerId"
          >
            <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-2">Stripe Customer ID</label>
            <div>
              <code class="small">{{ organization.stripeCustomerId }}</code>
              <div class="text-muted small mt-1">
                <em>Legacy field — Hub has not yet been upgraded to return paymentAccounts.</em>
              </div>
            </div>
          </div>
          <ng-container *ngIf="organization.paymentAccounts?.connect as connect">
            <div class="list-group-item bg-transparent px-0 py-3" *ngIf="connect.stripeAccountId">
              <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-2">Stripe Connect Account</label>
              <div>
                <code class="small">{{ connect.stripeAccountId }}</code>
                <div class="text-muted small mt-1">
                  Status: {{ connect.status }}
                  &nbsp;·&nbsp;
                  Charges: {{ connect.chargesEnabled ? 'on' : 'off' }}
                  &nbsp;·&nbsp;
                  Payouts: {{ connect.payoutsEnabled ? 'on' : 'off' }}
                </div>
              </div>
            </div>
          </ng-container>
        </div>
      </div>
    </div>

    <!-- Membership Card (ADN-2513) -->
    <div class="col-lg-6 mb-4">
      <app-organization-membership-card
        [organizationId]="organization.id"
        [organizationName]="organization.name ?? ''"
        [membership]="organization.membership ?? null"
        (changed)="membershipChanged.emit($event)"
      ></app-organization-membership-card>
    </div>

    <!-- Leadership Card -->
    <div class="col-lg-6 mb-4">
      <div class="card border-0 bg-transparent shadow-none">
        <div class="card-header bg-transparent px-0">
          <h6 class="mb-0">
            <i class="bi bi-people-fill me-2"></i>
            Leadership
          </h6>
        </div>
        <div class="card-body px-0 list-group list-group-flush">
          <div class="list-group-item bg-transparent px-0 py-3">
            <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-2">Owner</label>
            <div *ngIf="ownerProfile">
              <div class="d-flex align-items-center">
                <div class="entity-avatar me-2">
                  {{ ownerProfile.firstName.charAt(0) }}{{ ownerProfile.lastName.charAt(0) }}
                </div>
                <div>
                  <div class="fw-medium">{{ ownerProfile.firstName }} {{ ownerProfile.lastName }}</div>
                  <div class="text-muted small">{{ ownerProfile.email }}</div>
                </div>
              </div>
            </div>
            <div *ngIf="!ownerProfile" class="text-muted">
              Not assigned
            </div>
          </div>
          <div class="list-group-item bg-transparent px-0 py-3">
            <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-2">Chief Supervisor</label>
            <div *ngIf="chiefSupervisorProfile">
              <div class="d-flex align-items-center">
                <div class="entity-avatar me-2">
                  {{ chiefSupervisorProfile.firstName.charAt(0) }}{{ chiefSupervisorProfile.lastName.charAt(0) }}
                </div>
                <div>
                  <div class="fw-medium">{{ chiefSupervisorProfile.firstName }} {{ chiefSupervisorProfile.lastName }}</div>
                  <div class="text-muted small">{{ chiefSupervisorProfile.email }}</div>
                </div>
              </div>
            </div>
            <div *ngIf="!chiefSupervisorProfile" class="text-muted">
              Not assigned
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Quick Stats Card -->
    <div class="col-12">
      <div class="card border-0 bg-transparent shadow-none">
        <div class="card-header bg-transparent px-0">
          <h6 class="mb-0">
            <i class="bi bi-bar-chart-line me-2"></i>
            Quick Stats
          </h6>
        </div>
        <div class="card-body px-0">
          <div class="row">
            <!-- Members Stats -->
            <div class="col-md-3 mb-3">
              <div class="stat-card d-flex align-items-start gap-3 p-2">
                <div class="stat-icon bg-primary bg-opacity-10 text-primary">
                  <i class="bi bi-people"></i>
                </div>
                <div class="flex-grow-1">
                  <div class="small fw-semibold text-uppercase text-body-secondary mb-1">Members</div>
                  <div class="fs-5 fw-semibold mb-1">{{ stats?.activeMembers || 0 }} active</div>
                  <div class="small text-muted" *ngIf="stats && stats.inactiveMembers > 0">
                    {{ stats.inactiveMembers }} inactive
                  </div>
                </div>
              </div>
            </div>

            <!-- Drones Stats -->
            <div class="col-md-3 mb-3">
              <div class="stat-card d-flex align-items-start gap-3 p-2">
                <div class="stat-icon bg-info bg-opacity-10 text-info">
                  <i class="bi bi-truck"></i>
                </div>
                <div class="flex-grow-1">
                  <div class="small fw-semibold text-uppercase text-body-secondary mb-1">Drones</div>
                  <div class="fs-5 fw-semibold mb-1">{{ stats?.activeDrones || 0 }} active</div>
                  <div class="small text-muted" *ngIf="stats && stats.inactiveDrones > 0">
                    {{ stats.inactiveDrones }} inactive
                  </div>
                </div>
              </div>
            </div>

            <!-- Certifications Stats -->
            <div class="col-md-3 mb-3">
              <div class="stat-card d-flex align-items-start gap-3 p-2">
                <div class="stat-icon bg-success bg-opacity-10 text-success">
                  <i class="bi bi-patch-check"></i>
                </div>
                <div class="flex-grow-1">
                  <div class="small fw-semibold text-uppercase text-body-secondary mb-1">Certifications</div>
                  <div class="fs-5 fw-semibold mb-1">{{ stats?.validCertifications || 0 }} valid</div>
                  <div class="small" *ngIf="stats && stats.expiringSoonCertifications > 0">
                    <span class="text-warning">{{ stats.expiringSoonCertifications }} expiring soon</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Last Activity Stats -->
            <div class="col-md-3 mb-3">
              <div class="stat-card d-flex align-items-start gap-3 p-2">
                <div class="stat-icon bg-secondary bg-opacity-10 text-secondary">
                  <i class="bi bi-clock-history"></i>
                </div>
                <div class="flex-grow-1">
                  <div class="small fw-semibold text-uppercase text-body-secondary mb-1">Last Activity</div>
                  <div class="fs-5 fw-semibold mb-1">{{ formatTimeAgo(stats?.lastActivityAt || null) }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>


<!-- coordinates lookup confirmation modal that shows the found coordinates plus a map  -->
<ng-container *ngIf="pendingCoordinates">
  <div class="modal-backdrop fade show" (click)="declinedCoordinates()"></div>
  <div class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
    <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title"><i class="bi bi-geo-alt me-2"></i>Confirm Coordinates</h5>
          <button type="button" class="btn-close" (click)="declinedCoordinates()" [disabled]="savingCoordinates" aria-label="Close"></button>
        </div>

        <div class="modal-body">
          <p class="text-body-secondary small mb-3">
            Found from: {{ organization?.streetAddressLine1 }}, {{ organization?.city }}, {{ organization?.state }} {{ organization?.zipCode }}
          </p>

          <div class="row g-3 mb-3">
            <div class="col-sm-6">
              <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-1">Current</label>
              <div *ngIf="organization?.latitude != null; else notSet">
                {{ organization?.latitude | number:'1.5-5' }}, {{ organization?.longitude | number:'1.5-5' }}
              </div>
              <ng-template #notSet><div class="text-body-secondary fst-italic">Not set</div></ng-template>
            </div>
            <div class="col-sm-6">
              <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-1">New</label>
              <div class="fw-semibold">
                {{ pendingCoordinates.lat | number:'1.5-5' }}, {{ pendingCoordinates.lng | number:'1.5-5' }}
              </div>
            </div>
          </div>

          <div class="rounded-2 border overflow-hidden">
            <google-map
              [center]="pendingCoordinates"
              [zoom]="15"
              [options]="mapOptions"
              width="100%"
              height="320px">
              <map-marker [position]="pendingCoordinates"></map-marker>
            </google-map>
          </div>

          <div *ngIf="saveError" class="alert alert-danger d-flex align-items-start mt-3 mb-0" role="alert">
            <i class="bi bi-x-circle-fill me-2 mt-1"></i>
            <div>
              <div class="fw-semibold">Save failed</div>
              <div class="small">{{ saveError }}</div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" (click)="declinedCoordinates()" [disabled]="savingCoordinates">
            Decline
          </button>
          <button type="button" class="btn btn-primary" (click)="confirmedCoordinates()" [disabled]="savingCoordinates">
            <span *ngIf="savingCoordinates" class="spinner-border spinner-border-sm me-2" role="status"></span>
            {{ savingCoordinates ? 'Saving...' : 'Confirm' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</ng-container>

<!-- manual coordinate entry for failed lookup call  -->
<ng-container *ngIf="pendingManualCoordinates">
  <div class="modal-backdrop fade show" (click)="declinedCoordinates()"></div>
  <div class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
    <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title"><i class="bi bi-geo-alt me-2"></i>Manual Coordinate Entry</h5>
          <button type="button" class="btn-close" (click)="declinedCoordinates()" [disabled]="savingCoordinates" aria-label="Close"></button>
        </div>

        <div class="modal-body">
          <div class="alert alert-warning d-flex align-items-start mb-3" role="alert">
            <i class="bi bi-exclamation-triangle-fill me-2 mt-1"></i>
            <div>
              <div class="fw-semibold">Lookup failed</div>
              <div class="small">{{ lookupError }} Enter the latitude and longitude manually below.</div>
            </div>
          </div>

          <div class="row g-3 mb-3">
            <div class="col-sm-6">
              <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-1">Current</label>
              <div *ngIf="organization?.latitude != null; else notSet">
                {{ organization?.latitude | number:'1.5-5' }}, {{ organization?.longitude | number:'1.5-5' }}
              </div>
              <ng-template #notSet><div class="text-body-secondary fst-italic">Not set</div></ng-template>
            </div>
            <div class="col-sm-6">
              <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-1">New</label>
              <input
                type="number"
                class="form-control"
                placeholder="Latitude"
                min="-90" max="90" step="any" required
                #latInput="ngModel"
                [ngModel]="pendingManualCoordinates.lat"
                (ngModelChange)="updateManualCoordinate('lat', $event)"
                [class.is-invalid]="latInput.invalid && latInput.touched"
              />
              <div *ngIf="latInput.invalid && latInput.touched" class="invalid-feedback">
                Please enter a valid latitude
              </div>
              <input
                type="number"
                class="form-control mt-2"
                placeholder="Longitude"
                min="-180" max="180" step="any" required
                #lngInput="ngModel"
                [ngModel]="pendingManualCoordinates.lng"
                (ngModelChange)="updateManualCoordinate('lng', $event)"
                [class.is-invalid]="lngInput.invalid && lngInput.touched"
              />
              <div *ngIf="lngInput.invalid && lngInput.touched" class="invalid-feedback">
                Please enter a valid longitude
              </div>
            </div>
          </div>

          <div class="rounded-2 border overflow-hidden">
            <google-map
              [center]="pendingManualCoordinates"
              [zoom]="15"
              [options]="mapOptions"
              width="100%"
              height="320px">
              <map-marker [position]="pendingManualCoordinates"></map-marker>
            </google-map>
          </div>

          <div *ngIf="saveError" class="alert alert-danger d-flex align-items-start mt-3 mb-0" role="alert">
            <i class="bi bi-x-circle-fill me-2 mt-1"></i>
            <div>
              <div class="fw-semibold">Save failed</div>
              <div class="small">{{ saveError }}</div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" (click)="declinedCoordinates()" [disabled]="savingCoordinates">
            Decline
          </button>
          <button 
            type="button" 
            class="btn btn-primary" 
            (click)="confirmedCoordinates()" 
            [disabled]="savingCoordinates || latInput.invalid || lngInput.invalid">
            <span *ngIf="savingCoordinates" class="spinner-border spinner-border-sm me-2" role="status"></span>
            {{ savingCoordinates ? 'Saving...' : 'Confirm' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</ng-container>